import { useTranslation } from 'react-i18next';
import type { Participant } from '@/types/evaluation.types';
import { ParticipantCard } from './ParticipantCard';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Users, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ParticipantListProps {
  participants: Participant[];
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  onResendInvitation: (id: string) => void;
  onAddClick: () => void;
}

/**
 * List of participants with empty state
 */
export function ParticipantList({
  participants,
  onRemove,
  onEdit,
  onResendInvitation,
  onAddClick,
}: ParticipantListProps) {
  const { t } = useTranslation(['participants', 'common']);

  // Find the primary host (first participant with Host type)
  const primaryHostId = participants.find((p) => p.participantType === 'Host')?.id;

  // Show empty state if only the host exists
  if (participants.length === 1 && participants[0].participantType === 'Host') {
    return (
      <div className="space-y-6">
        {/* Show the host card */}
        <div>
          <h2 className="text-lg font-semibold mb-4">{t('participants:currentParticipants')}</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <ParticipantCard
              participant={participants[0]}
              isPrimaryHost={true}
              onRemove={onRemove}
              onEdit={onEdit}
              onResendInvitation={onResendInvitation}
            />
          </div>
        </div>

        {/* Empty state alert */}
        <Alert>
          <Users className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{t('participants:emptyState.title')}</AlertTitle>
          <AlertDescription className="mt-2 space-y-3">
            <p>{t('participants:emptyState.description')}</p>
            <Button onClick={onAddClick} size="sm">
              <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('participants:actions.inviteParticipant')}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show participant list
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {t('participants:currentParticipants')} ({participants.length})
        </h2>
        <Button onClick={onAddClick} size="sm">
          <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('participants:actions.inviteParticipant')}
        </Button>
      </div>

      {/* Grid of participant cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {participants.map((participant) => (
          <ParticipantCard
            key={participant.id}
            participant={participant}
            isPrimaryHost={participant.id === primaryHostId}
            onRemove={onRemove}
            onEdit={onEdit}
            onResendInvitation={onResendInvitation}
          />
        ))}
      </div>
    </div>
  );
}
